<template>
  <div class="password_container">
    <form @submit.prevent="updatePassword()" class="password_container__form">
      <template v-if="horizontal_view">
        <div v-if="show_email_field" class="d-flex flex-row">
          <div class="password_container__input flex-fill">
            <label
              for="email_address"
              class="password_container__input_label"
              :class="
                emailCheckResponse ? 'password_container__is_invalid' : ''
              "
            >
              {{ email_label }}
            </label>
            <input
              id="email_address"
              name="email_address"
              :class="
                emailCheckResponse ? 'password_container__input_is_invalid' : ''
              "
              type="text"
              v-model="email_address"
              required
              autofocus
            />
            <FormError
              v-if="emailCheckResponse"
              :errorMessage="emailCheckResponse"
            />
          </div>
        </div>
        <div v-if="show_current_password_field" class="d-flex flex-row">
          <div class="password_container__input flex-fill">
            <label
              for="current_password"
              class="password_container__input_label"
              :class="
                current_password_error_message
                  ? 'password_container__is_invalid'
                  : ''
              "
            >
              Current Password
            </label>
            <div class="password_container__input_container">
              <input
                id="current_password"
                name="current_password"
                :class="
                  current_password_error_message
                    ? 'password_container__input_is_invalid'
                    : ''
                "
                :type="currentPasswordFieldType"
                v-model="currentPassword"
                required
              />
              <img
                :src="currentPasswordIconImage"
                class="password_container__toggle_icon"
                @mousedown="showPasswordCurrent"
                @mouseup="hidePasswordCurrent"
                @mouseleave="hidePasswordCurrent"
              />
            </div>
            <FormError
              v-if="current_password_error_message"
              :errorMessage="current_password_error_message"
            />
          </div>
        </div>
        <div class="password_container__gap d-flex flex-row flex-wrap">
          <div class="password_container__input flex-fill">
            <label
              for="password"
              class="password_container__input_label"
              :class="
                password_error_message ? 'password_container__is_invalid' : ''
              "
            >
              {{ password_label }}
            </label>
            <div class="password_container__input_container">
              <input
                id="password"
                name="password"
                :class="
                  password_error_message
                    ? 'password_container__input_is_invalid'
                    : ''
                "
                :type="passwordFieldType"
                v-model="password"
                required
              />
              <img
                :src="passwordIconImage"
                class="password_container__toggle_icon"
                @mousedown="showPassword"
                @mouseup="hidePassword"
                @mouseleave="hidePassword"
              />
            </div>
            <FormError
              v-if="password_error_message"
              :errorMessage="password_error_message"
            />
          </div>
          <div class="password_container__input flex-fill">
            <label
              for="password_confirmation"
              class="password_container__input_label"
              :class="
                password_confirm_error_message
                  ? 'password_container__is_invalid'
                  : ''
              "
            >
              {{ password_confirm_label }}
            </label>
            <div class="password_container__input_container">
              <input
                id="password_confirmation"
                name="password_confirmation"
                :class="
                  password_confirm_error_message
                    ? 'password_container__input_is_invalid'
                    : ''
                "
                :type="passwordConfirmFieldType"
                v-model="passwordConfirm"
                required
              />
              <img
                :src="passwordConfirmIconImage"
                class="password_container__toggle_icon"
                @mousedown="showPasswordConfirm"
                @mouseup="hidePasswordConfirm"
                @mouseleave="hidePasswordConfirm"
              />
            </div>
            <FormError
              v-if="password_confirm_error_message"
              :errorMessage="password_confirm_error_message"
            />
          </div>
        </div>
        <div class="password_container__password_check_horizontal">
          <template v-if="showPasswordMatchText">
            <p
              class="m-0"
              :class="
                passwordsMatch
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              Passwords do match
            </p>
          </template>
          <template v-else>
            <p
              class="m-0"
              :class="
                minCharacterPass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              Minimum of 11 characters
            </p>
            <p
              class="m-0"
              :class="
                upperCasePass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              One uppercase letter
            </p>
            <p
              class="m-0"
              :class="
                lowerCasePass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              One lowercase letter
            </p>
            <p
              class="m-0"
              :class="
                oneNumberPass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              One number
            </p>
            <p
              class="m-0"
              :class="
                specialCharacterPass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              One special character
            </p>
            <p
              v-if="enable_previous_password_check"
              class="m-0"
              :class="
                notLastThreePass
                  ? 'password_container__valid_password_parameter'
                  : 'password_container__invalid_password_parameter'
              "
            >
              Cannot be same as last three passwords
            </p>
          </template>
        </div>
      </template>
      <template v-else>
        <div class="row password_container__row">
          <div class="col-xxx-large-6 pt-5">
            <p
              v-if="show_password_message"
              class="password_container__password_message"
            >
              A strong password helps prevent unauthorized access to your
              onDemand account. The requirements are as follows:
            </p>
            <div class="password_container__password_check">
              <template v-if="showPasswordMatchText">
                <p
                  class="m-0"
                  :class="
                    passwordsMatch
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  Passwords do match
                </p>
              </template>
              <template v-else>
                <p
                  class="m-0"
                  :class="
                    minCharacterPass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  Minimum of 11 characters
                </p>
                <p
                  class="m-0"
                  :class="
                    upperCasePass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  One uppercase letter
                </p>
                <p
                  class="m-0"
                  :class="
                    lowerCasePass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  One lowercase letter
                </p>
                <p
                  class="m-0"
                  :class="
                    oneNumberPass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  One number
                </p>
                <p
                  class="m-0"
                  :class="
                    specialCharacterPass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  One special character
                </p>
                <p
                  v-if="enable_previous_password_check"
                  class="m-0"
                  :class="
                    notLastThreePass
                      ? 'password_container__valid_password_parameter'
                      : 'password_container__invalid_password_parameter'
                  "
                >
                  Cannot be same as last three passwords
                </p>
              </template>
            </div>
          </div>
          <div
            class="col-xxx-large-6"
            :class="
              !show_email_field && !show_current_password_field ? 'pt-5' : ''
            "
          >
            <div v-if="show_email_field" class="password_container__input">
              <label
                for="email_address"
                class="password_container__input_label"
                :class="
                  emailCheckResponse ? 'password_container__is_invalid' : ''
                "
              >
                {{ email_label }}
              </label>
              <input
                id="email_address"
                name="email_address"
                :class="
                  emailCheckResponse
                    ? 'password_container__input_is_invalid'
                    : ''
                "
                type="text"
                v-model="email_address"
                required
                autofocus
              />
              <FormError
                v-if="emailCheckResponse"
                :errorMessage="emailCheckResponse"
              />
            </div>
            <div
              v-if="show_current_password_field"
              class="password_container__input"
            >
              <label
                for="current_password"
                class="password_container__input_label"
                :class="
                  current_password_error_message
                    ? 'password_container__is_invalid'
                    : ''
                "
              >
                Current Password
              </label>
              <div class="password_container__input_container">
                <input
                  id="current_password"
                  name="current_password"
                  :class="
                    current_password_error_message
                      ? 'password_container__input_is_invalid'
                      : ''
                  "
                  :type="currentPasswordFieldType"
                  v-model="currentPassword"
                  required
                />
                <img
                  :src="currentPasswordIconImage"
                  class="password_container__toggle_icon"
                  @mousedown="showPasswordCurrent"
                  @mouseup="hidePasswordCurrent"
                  @mouseleave="hidePasswordCurrent"
                />
              </div>
              <FormError
                v-if="current_password_error_message"
                :errorMessage="current_password_error_message"
              />
            </div>
            <div class="password_container__input">
              <label
                for="password"
                class="password_container__input_label"
                :class="
                  password_error_message ? 'password_container__is_invalid' : ''
                "
              >
                {{ password_label }}
              </label>
              <div class="password_container__input_container">
                <input
                  id="password"
                  name="password"
                  :class="
                    password_error_message
                      ? 'password_container__input_is_invalid'
                      : ''
                  "
                  :type="passwordFieldType"
                  v-model="password"
                  required
                />
                <img
                  :src="passwordIconImage"
                  class="password_container__toggle_icon"
                  @mousedown="showPassword"
                  @mouseup="hidePassword"
                  @mouseleave="hidePassword"
                />
              </div>
              <FormError
                v-if="password_error_message"
                :errorMessage="password_error_message"
              />
            </div>
            <div class="password_container__input">
              <label
                for="password_confirmation"
                class="password_container__input_label"
                :class="
                  password_confirm_error_message
                    ? 'password_container__is_invalid'
                    : ''
                "
              >
                {{ password_confirm_label }}
              </label>
              <div class="password_container__input_container">
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  :class="
                    password_confirm_error_message
                      ? 'password_container__input_is_invalid'
                      : ''
                  "
                  :type="passwordConfirmFieldType"
                  v-model="passwordConfirm"
                  required
                />
                <img
                  :src="passwordConfirmIconImage"
                  class="password_container__toggle_icon"
                  @mousedown="showPasswordConfirm"
                  @mouseup="hidePasswordConfirm"
                  @mouseleave="hidePasswordConfirm"
                />
              </div>
              <FormError
                v-if="password_confirm_error_message"
                :errorMessage="password_confirm_error_message"
              />
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="show_cancel_button || show_submit_button"
        class="password_container__gap d-flex flex-row flex-wrap-reverse justify-content-center py-3"
      >
        <button
          v-if="show_cancel_button"
          type="button"
          class="btn button-grey px-5 width-sm-100"
          @click="cancelUpdateRequest()"
        >
          Cancel
        </button>
        <button
          v-if="show_submit_button"
          type="submit"
          class="btn px-5 width-sm-100"
          :disabled="
            !emailValid ||
            !passwordsMatch ||
            !showPasswordMatchText ||
            (show_current_password_field && currentPassword === '') ||
            is_loading
              ? true
              : false
          "
          :class="
            !emailValid ||
            !passwordsMatch ||
            !showPasswordMatchText ||
            (show_current_password_field && currentPassword === '') ||
            is_loading
              ? 'button-grey'
              : 'button-green'
          "
        >
          <template v-if="is_loading">
            <small style="font-weight: bold">Saving...</small>
          </template>
          <template v-else>Reset Password</template>
        </button>
      </div>
      <div
        v-if="updatePasswordResponse.message"
        class="d-flex justify-content-center py-3"
      >
        <p
          :class="
            updatePasswordResponse.valid
              ? 'password_container__success_message'
              : 'password_container__error_message'
          "
        >
          {{ updatePasswordResponse.message }}
        </p>
      </div>
    </form>
  </div>
</template>

<script>
  import axios from 'axios';
  import FormError from '~/components/form/FormError.vue';
  import _ from 'lodash';

  export default {
    name: 'PasswordComponent',
    emits: [
      'end-password-update-process',
      'email-update',
      'password-update',
      'password-confirm-update',
    ],
    components: {
      FormError,
    },
    props: {
      user_email: {
        type: String,
        required: false,
        default: '',
      },
      password_reset_token: {
        type: String,
        required: false,
        default: '',
      },
      redirect: {
        type: String,
        required: false,
        default: '',
      },
      show_password_message: {
        type: Boolean,
        required: false,
        default: true,
      },
      show_cancel_button: {
        type: Boolean,
        required: false,
        default: true,
      },
      show_email_field: {
        type: Boolean,
        required: false,
        default: false,
      },
      show_submit_button: {
        type: Boolean,
        required: false,
        default: true,
      },
      show_current_password_field: {
        type: Boolean,
        required: false,
        default: false,
      },
      horizontal_view: {
        type: Boolean,
        required: false,
        default: false,
      },
      enable_previous_password_check: {
        type: Boolean,
        required: false,
        default: true,
      },
      email_label: {
        type: String,
        required: false,
        default: 'Email Address',
      },
      password_label: {
        type: String,
        required: false,
        default: 'New Password',
      },
      password_confirm_label: {
        type: String,
        required: false,
        default: 'Confirm Password',
      },
      email_error_message: {
        type: String,
        required: false,
        default: '',
      },
      password_error_message: {
        type: String,
        required: false,
        default: '',
      },
      password_confirm_error_message: {
        type: String,
        required: false,
        default: '',
      },
    },
    data() {
      return {
        email_address: this.user_email,
        password: '',
        passwordConfirm: '',
        currentPassword: '',
        is_loading: false,
        emailNotValid: false,
        passwordVisible: false,
        passwordConfirmVisible: false,
        notLastThreePass: false,
        passwordFieldType: 'password',
        passwordConfirmFieldType: 'password',
        currentPasswordFieldType: 'password',
        current_password_error_message: '',
        passwordIconImage: '/images/eyeball-open.png',
        passwordConfirmIconImage: '/images/eyeball-open.png',
        currentPasswordIconImage: '/images/eyeball-open.png',
        updatePasswordResponse: {
          message: '',
          valid: false,
        },
      };
    },
    watch: {
      email_address: {
        handler: function () {
          this.email_address = this.email_address.trim();
          this.$emit('email-update', this.email_address);
        },
        immediate: true,
      },
      password: {
        handler: function () {
          this.notLastThreePass = false;
          this.$emit('password-update', this.password);
          if (this.enable_previous_password_check && this.password !== '') {
            this.debounceCheckPastPasswords();
          }
        },
        immediate: false,
      },
      passwordConfirm: {
        handler: function () {
          this.$emit('password-confirm-update', this.passwordConfirm);
        },
        immediate: true,
      },
    },
    computed: {
      emailCheckResponse() {
        if (this.email_error_message) {
          return this.email_error_message;
        }

        if (this.email_address === '') {
          return '';
        }

        if (!this.emailValid) {
          return 'Please enter a valid email address';
        }

        return '';
      },
      emailValid() {
        // check email is valid
        if (this.email_address.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          return true;
        }

        return false;
      },
      minCharacterPass() {
        // check password is at least 11 characters
        if (this.password.length >= 11) {
          return true;
        }

        return false;
      },
      upperCasePass() {
        // check password has one upper case character
        if (this.password.match(/[A-Z]/)) {
          return true;
        }

        return false;
      },
      lowerCasePass() {
        // check password has one lower case character
        if (this.password.match(/[a-z]/)) {
          return true;
        }

        return false;
      },
      oneNumberPass() {
        // check password has one number
        if (this.password.match(/[0-9]/)) {
          return true;
        }

        return false;
      },
      specialCharacterPass() {
        // check password has one special character
        if (this.password.match(/[^a-zA-Z0-9]/)) {
          return true;
        }

        return false;
      },
      passwordsMatch() {
        // check password and confirm password match
        if (
          this.password !== '' &&
          this.passwordConfirm !== '' &&
          this.password === this.passwordConfirm
        ) {
          return true;
        }

        return false;
      },
      showPasswordMatchText() {
        // if all other computed fields are true then user can edit confirm password field
        if (
          this.minCharacterPass &&
          this.upperCasePass &&
          this.lowerCasePass &&
          this.oneNumberPass &&
          this.specialCharacterPass &&
          (!this.enable_previous_password_check ||
            (this.enable_previous_password_check && this.notLastThreePass))
        ) {
          return true;
        }

        return false;
      },
      currentPasswordTrimmed() {
        return this.currentPassword.trim();
      },
    },
    methods: {
      cancelUpdateRequest() {
        if (this.redirect) {
          window.location = this.redirect;
        } else {
          this.$emit('end-password-update-process');
        }
      },
      showPassword() {
        this.passwordFieldType = 'text';
        this.passwordIconImage = '/images/eyeball-closed.png';
      },
      hidePassword() {
        this.passwordFieldType = 'password';
        this.passwordIconImage = '/images/eyeball-open.png';
      },
      showPasswordConfirm() {
        this.passwordConfirmFieldType = 'text';
        this.passwordConfirmIconImage = '/images/eyeball-closed.png';
      },
      hidePasswordConfirm() {
        this.passwordConfirmFieldType = 'password';
        this.passwordConfirmIconImage = '/images/eyeball-open.png';
      },
      showPasswordCurrent() {
        this.currentPasswordFieldType = 'text';
        this.currentPasswordIconImage = '/images/eyeball-closed.png';
      },
      hidePasswordCurrent() {
        this.currentPasswordFieldType = 'password';
        this.currentPasswordIconImage = '/images/eyeball-open.png';
      },
      debounceCheckPastPasswords: _.debounce(async function () {
        this.notLastThreePass = await this.checkPastPasswords();
      }, 1000),
      async checkPastPasswords() {
        try {
          const response = await axios.post(
            '/password/check-previous-passwords',
            {
              password_reset_token: this.password_reset_token,
              password: this.password,
              email: this.email_address,
            }
          );

          return response.data;
        } catch (error) {
          return false;
        }
      },
      async updatePassword() {
        this.message = '';
        this.is_loading = true;
        this.current_password_error_message = '';
        this.updatePasswordResponse = {
          message: '',
          valid: false,
        };

        if (
          this.show_current_password_field &&
          this.currentPasswordTrimmed === ''
        ) {
          this.current_password_error_message =
            'Please enter your current password';
          this.is_loading = false;
          return;
        }

        try {
          const response = await axios.post('/password/update', {
            password: this.password,
            email: this.email_address,
            password_reset_token: this.password_reset_token,
            current_password: this.currentPassword,
            validate_current_password: this.show_current_password_field,
          });

          this.updatePasswordResponse = {
            message: response.data.message,
            valid: response.data.valid,
          };

          if (response.data.valid) {
            if (this.redirect) {
              this.is_loading = false;
              window.location = this.redirect;
            } else {
              //wait a few seconds then call emit
              setTimeout(() => {
                this.is_loading = false;
                this.$emit('end-password-update-process');
              }, 2000);
            }
          } else {
            this.is_loading = false;
          }
        } catch (error) {
          this.is_loading = false;
          this.message = error.response.data.message;
          this.validResponse = false;
          this.updatePasswordResponse = {
            message: error.response.data.message,
            valid: false,
          };
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .password_container {
    &__row {
      min-height: 300px;
    }

    &__gap {
      gap: 1rem;
    }

    &__form {
      display: block;
      margin-block-end: 0em;
    }

    &__password_message {
      color: #183961;
      font-family: 'Open Sans';
      font-size: 17px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    &__valid_password_parameter {
      color: #41ad49;
      font-family: 'Open Sans';
      font-size: 17px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      &::before {
        margin-right: 5px;
        content: '\2713';
      }
    }

    &__invalid_password_parameter {
      color: #d2232a;
      font-family: 'Open Sans';
      font-size: 17px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      &::before {
        margin-right: 5px;
        content: '\0078';
      }
    }

    &__password_check {
      padding-left: 2rem;
      padding-right: 2rem;
      padding-top: 1rem;
      padding-bottom: 1rem;
    }

    &__password_check_horizontal {
      padding: 1rem;
    }

    &__input {
      padding-top: 1rem;
    }

    &__input_label {
      color: #585b60;
      font-family: 'Open Sans';
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 122.563%; /* 19.61px */
      text-transform: capitalize;
    }

    &__input_container {
      position: relative;
    }

    &__toggle_icon {
      position: absolute;
      right: 15px;
      top: 50%;
      width: 27px;
      height: 21px;
      transform: translateY(-50%);
      cursor: pointer;
    }

    &__success_message {
      color: #41ad49;
    }

    &__error_message {
      color: #d2232a;
    }

    &__is_invalid {
      color: var(--itr-color-danger);
    }

    &__input_is_invalid {
      border-color: var(--itr-color-danger) !important;
    }
  }
</style>
